// Card view models (IMPLEMENTATION §3 M7, §2.7 "Tap semantics", UX §2 Info / Machine / Gallery / Crate / Ghost
// cards, UX §3.5). PURE: what each card shows and which commands its buttons send, built from the sim state
// through query.ts only (sale values, stall words, cycle times, consumers, pause / rotate: the sim's rules,
// never re-derived here), plus defs tables, copy.json and ui.json. The Preact cards only lay these out.
//   which card   an item (long-press) → Info; a rock cell → Expansion; a ghost → Ghost; Play: the Gallery →
//                Gallery card, a Crate → Crate card, other buildings → the Machine card as Info (Pause, Rotate,
//                "Edit in Build"); Build: every building → the Machine card (Rotate, Pause, Sell +$X / pinned),
//                a Crate → Crate card with Sell. Deposits are never tapped in Play (they are mined).
import type { IconKey } from '../../fx/icons.ts';
import type { Mode } from '../../input/modes.ts';
import type { Names } from '../../input/preview.ts';
import type { SimDefs } from '../../sim/defs.ts';
import type { BType, ExpansionId, FamId } from '../../sim/gen/ids.ts';
import {
  bottleneck,
  canPause,
  canRotate,
  crateSellValue,
  cycleMs,
  expansions,
  famConsumers,
  galleryStatus,
  ghostView,
  hasFeature,
  machineBusy,
  maxLevel,
  packerTarget,
  resolveDrop,
  stallDetail,
  stallOf,
} from '../../sim/query.ts';
import type { Building, CrateB, GalleryB, ProcessorB, SimState } from '../../sim/state.ts';
import { copyText, fillCopy } from '../copy.ts';
import { bottleneckAction, etaFromSec, etaText, type Fmt, money, perSec } from '../format.ts';

export type CardTarget =
  | { k: 'item'; id: number }
  | { k: 'building'; id: number }
  | { k: 'rock'; x: number; y: number };

export type CardKind = 'item' | 'machine' | 'gallery' | 'crate' | 'ghost' | 'expansion';

/** Which card a target opens in `mode` (null: nothing to show — the entity is gone, an open cell). */
export function cardKindOf(s: SimState, t: CardTarget, mode: Mode): CardKind | null {
  if (t.k === 'item') return s.floor.some((i) => i.id === t.id) ? 'item' : null;
  if (t.k === 'rock') return 'expansion';
  const b = s.buildings.find((x) => x.id === t.id);
  if (!b) return null;
  if (b.ghost !== null) return 'ghost';
  if (b.type === 'crate') return 'crate';
  if (b.type === 'gallery' && mode !== 'build') return 'gallery';
  return 'machine';
}

export type StatusTone = 'ok' | 'wait' | 'error' | 'paused';

const fmtSec = (s: number): string => (Math.round(s * 10) / 10).toFixed(1).replace(/\.0$/, '');

// ---- item Info card -------------------------------------------------------------------------------------

export interface ItemModel {
  kind: 'item';
  id: number;
  icon: IconKey;
  title: string;
  tier: number;
  maxTier: number;
  /** "Value $12 · sells $12" */
  value: string;
  /** "Merge 2 → Lead Boulder $28" (null at max tier). */
  mergeInto: string | null;
  /** "Used by: Caster, Crate" (null when nothing takes it). */
  usedBy: string | null;
}

function famUsers(d: SimDefs, names: Names, fam: FamId): string[] {
  const out: string[] = [];
  for (const t of famConsumers(d, fam)) {
    const n = names.building(t, null);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/**
 * "Value $12 · sells $12" from the counter drop the sim would resolve (R13): a pinned family (a v0.1 gun,
 * the last of a protected family) says why it can't be sold; before the counter is unlocked, only the value.
 */
function saleLine(
  f: Fmt,
  s: SimState,
  it: { id: number; x: number; y: number },
  value: number,
): string {
  const c = f.copy.cards;
  const out = resolveDrop(
    s,
    f.defs,
    { k: 'cell', x: it.x, y: it.y, id: it.id },
    { k: 'counter' },
    false,
  );
  if (out.k === 'sell')
    return fillCopy(c.value ?? '', { value: money(f, value), sells: money(f, out.value) });
  if (out.k === 'reject' && (out.reason === 'pinned' || out.reason === 'pinnedGun'))
    return fillCopy(c.valueNoSale ?? '{value}', {
      value: money(f, value),
      why: copyText(f.copy, `reason.${out.reason}`),
    });
  return fillCopy(c.valueOnly ?? '{value}', { value: money(f, value) });
}

export function itemModel(f: Fmt, s: SimState, id: number): ItemModel | null {
  const it = s.floor.find((i) => i.id === id);
  if (!it) return null;
  const d = f.defs;
  const fam = d.families.find((x) => x.id === it.fam);
  if (!fam) return null;
  const v = fam.values[it.tier - 1] ?? 0;
  const next = it.tier < fam.maxTier ? it.tier + 1 : null;
  const users = famUsers(d, f.names, it.fam);
  return {
    kind: 'item',
    id,
    icon: { k: 'item', fam: it.fam, tier: it.tier },
    title: f.names.item(it.fam, it.tier),
    tier: it.tier,
    maxTier: fam.maxTier,
    value: saleLine(f, s, it, v),
    mergeInto:
      next === null
        ? null
        : fillCopy(f.copy.cards.mergeInto ?? '', {
            name: f.names.item(it.fam, next),
            value: money(f, fam.values[next - 1] ?? 0),
          }),
    usedBy:
      users.length === 0
        ? null
        : fillCopy(f.copy.cards.usedBy ?? '', { machines: users.join(', ') }),
  };
}

// ---- Machine card (Info in Play) ------------------------------------------------------------------------

export interface MachineModel {
  kind: 'machine';
  bid: number;
  type: BType;
  icon: IconKey;
  title: string;
  level: number;
  maxLevel: number;
  build: boolean;
  status: string;
  tone: StatusTone;
  /** Stats lines: cycle / rate, heat, busy share, odds, inputs. */
  lines: string[];
  /** "Merge 2 → L2: 0.8 s per cycle" (null at max level / not a machine). */
  mergeHint: string | null;
  canRotate: boolean;
  /** 'pause' / 'resume', or null when it cannot be paused. */
  pause: 'pause' | 'resume' | null;
  /** Build mode: the Sell button (100% refund) or the pinned note; null in Play. */
  sell: { value: number; text: string } | { pinned: string } | null;
  /** Play: "Edit in Build" (once Build mode is unlocked). */
  editInBuild: boolean;
  /** Packer target picker: the chosen tier and the choices (2 … cap). */
  packer: { target: number; options: number[] } | null;
  /** A Loader: the R17 walk behind its starved Gallery ("Next: Upgrade Smelter"; R17 fallback). */
  advice: string | null;
}

/** The R17 walk of the first starved Gallery (v0.1: the one Gallery the Loader feeds), else null. */
function loaderAdvice(f: Fmt, s: SimState): string | null {
  for (const g of s.buildings) {
    if (g.type !== 'gallery' || g.ghost !== null) continue;
    if (galleryStatus(s, f.defs, g.id)?.binding !== 'starved') continue;
    const bn = bottleneck(s, f.defs, g.id);
    if (bn)
      return fillCopy(f.copy.gallery.advice ?? '{action}', { action: bottleneckAction(f, bn) });
  }
  return null;
}

function stallText(f: Fmt, s: SimState, b: Building): { text: string; tone: StatusTone } | null {
  const st = stallOf(s, f.defs, b.id);
  if (!st || st.stall === null || !st.shown) return null;
  const def = f.defs.stalls.find((x) => x.id === st.stall);
  const tone: StatusTone =
    def?.cls === 'error' ? 'error' : def?.cls === 'paused' ? 'paused' : 'wait';
  const vars: Record<string, string | number> = {};
  const det = stallDetail(s, f.defs, b.id);
  if (st.stall === 'NO_INPUT' && det) vars.x = det.inputs.map((x) => f.names.fam(x)).join(' / ');
  if (st.stall === 'MISMATCH' && det?.mismatch) {
    vars.fam = det.mismatch.lacking ? f.names.fam(det.mismatch.lacking) : '';
    vars.k = det.mismatch.k;
  }
  if (st.stall === 'GHOST') vars.items = ghostNeeds(f, s, b.id).join(', ');
  return { text: copyText(f.copy, `stall.${st.stall}`, vars), tone };
}

/** Seconds per cycle of a processor at `level` (query.cycleMs, R2), null for other types. */
export function cycleSeconds(d: SimDefs, type: Building['type'], level: number): number | null {
  const ms = cycleMs(d, type, level);
  return ms === null ? null : ms / 1000;
}

function machineLines(f: Fmt, s: SimState, b: Building): string[] {
  const d = f.defs;
  const c = f.copy.cards;
  const out: string[] = [];
  const cyc = cycleSeconds(d, b.type, b.level);
  if (cyc !== null) {
    out.push(
      `${fillCopy(c.cycle ?? '', { s: fmtSec(cyc) })} · ${fillCopy(c.throughput ?? '', { n: cyc > 0 ? Math.round(60 / cyc) : 0 })}`,
    );
  }
  if (b.type === 'drill') {
    const ticks = d.buildings.drill.hitTicks[b.level - 1] ?? 1;
    out.push(fillCopy(c.drillRate ?? '', { n: Math.round((60 * d.tickHz) / ticks) }));
  }
  if (b.type === 'deposit') {
    const odds = d.depositRules.oddsByLevel[b.level - 1] ?? [];
    const dep = d.deposits.find((x) => x.id === b.ore);
    const parts = odds
      .filter((o) => o.permille > 0)
      .map((o) =>
        fillCopy(c.odds ?? '', {
          pct: Math.round(o.permille / 10),
          name: dep ? f.names.item(dep.fam, o.tier) : `T${o.tier}`,
        }),
      );
    out.push(`${c.spawns ?? ''} ${parts.join(' · ')}`.trim());
  }
  if (b.type === 'smelter') {
    const cap = d.buildings.smelter.heatCap[b.level - 1] ?? 0;
    out.push(fillCopy(c.heat ?? '', { n: (b as ProcessorB).heat, cap }));
  }
  if (b.type === 'packer') {
    out.push(fillCopy(c.buffer ?? '', { n: b.buf.length, cap: d.buildings.packer.buffer }));
  }
  const busy = machineBusy(s, d, b.id);
  if (busy !== null) out.push(fillCopy(c.busy ?? '', { pct: Math.round(busy / 10) }));
  return out;
}

export function machineModel(f: Fmt, s: SimState, bid: number, mode: Mode): MachineModel | null {
  const b = s.buildings.find((x) => x.id === bid);
  if (!b) return null;
  const d = f.defs;
  const c = f.copy.cards;
  const build = mode === 'build';
  const maxLv = maxLevel(d, b.type);
  const stall = stallText(f, s, b);
  const status = stall ?? {
    text: b.paused ? (f.copy.stall.PAUSED ?? '') : (c.working ?? ''),
    tone: (b.paused ? 'paused' : 'ok') as StatusTone,
  };
  let mergeHint: string | null = null;
  if (b.level < maxLv) {
    const cyc = cycleSeconds(d, b.type, b.level + 1);
    mergeHint =
      cyc !== null
        ? fillCopy(c.mergeHint ?? '', { n: b.level + 1, s: fmtSec(cyc) })
        : fillCopy(c.level ?? 'L{n}', { n: b.level + 1 });
  }
  let sell: MachineModel['sell'] = null;
  if (build) {
    const out = resolveDrop(s, d, { k: 'cell', x: b.x, y: b.y, id: b.id }, { k: 'sell' }, true);
    if (out.k === 'bsell')
      sell = {
        value: out.refund,
        text: fillCopy(c.sellFor ?? '', { value: money(f, out.refund) }),
      };
    else if (out.k === 'reject' && out.reason === 'pinned') sell = { pinned: c.cantSell ?? '' };
  }
  let packer: MachineModel['packer'] = null;
  const pt = packerTarget(s, d, bid);
  if (pt !== null) {
    const options: number[] = [];
    for (let t = 2; t <= pt.cap; t++) options.push(t);
    packer = { target: pt.target, options };
  }
  return {
    kind: 'machine',
    bid,
    type: b.type,
    icon: { k: 'building', type: b.type, ore: b.type === 'deposit' ? b.ore : null, level: b.level },
    title: f.names.building(b.type, b.type === 'deposit' ? b.ore : null),
    level: b.level,
    maxLevel: maxLv,
    build,
    status: status.text,
    tone: status.tone,
    lines: machineLines(f, s, b),
    mergeHint,
    canRotate: canRotate(s, d, bid),
    pause: canPause(s, d, bid) ? (b.paused ? 'resume' : 'pause') : null,
    sell,
    editInBuild: !build && hasFeature(s, d, 'buildMode'),
    packer,
    advice: b.type === 'loader' ? loaderAdvice(f, s) : null,
  };
}

// ---- Gallery card ---------------------------------------------------------------------------------------

export type BindingKind = 'starved' | 'gunLimited' | 'firing' | 'noGun';

export interface GalleryModel {
  kind: 'gallery';
  bid: number;
  title: string;
  gun: { icon: IconKey; line: string } | null;
  /** Ammo queue tiers (head first) and its capacity. */
  queue: { tiers: number[]; cap: number; text: string };
  /** Practice rounds left (the cold-open tin), null once practice is over. */
  practice: number | null;
  income: string;
  binding: BindingKind;
  bindingText: string;
  bindingLine: string;
  tip: string | null;
  /** The R17 walk behind a STARVED Gallery ("Upgrade Smelter" / "Feed Smelter: needs Copper"). */
  advice: string | null;
}

export function galleryModel(f: Fmt, s: SimState, bid: number): GalleryModel | null {
  const b = s.buildings.find((x) => x.id === bid);
  if (b?.type !== 'gallery') return null;
  const g = b as GalleryB;
  const d = f.defs;
  const gc = f.copy.gallery;
  const st = galleryStatus(s, d, bid);
  if (!st) return null;
  const rate = (milli: number): string => fmtSec(milli / 1000);
  let gun: GalleryModel['gun'] = null;
  if (g.gun) {
    const gt = d.guns[g.gun.tier - 1];
    gun = {
      icon: { k: 'item', fam: g.gun.fam, tier: g.gun.tier },
      line: fillCopy(gc.gunLine ?? '', {
        gun: f.names.item(g.gun.fam, g.gun.tier),
        mult: gt ? (gt.multPermille / 1000).toFixed(2) : '1.00',
        s: gt ? fmtSec(gt.fireTicks / d.tickHz) : '',
      }),
    };
  }
  const cap = d.buildings.gallery.ammoQueue;
  const binding: BindingKind = st.binding;
  const bindingText =
    binding === 'starved'
      ? (gc.starved ?? '')
      : binding === 'gunLimited'
        ? (gc.gunLimited ?? '')
        : binding === 'noGun'
          ? (gc.noGun ?? '')
          : (gc.firing ?? '');
  const vars = { fire: rate(st.fireRateMilli), supply: rate(st.supplyRateMilli) };
  const bindingLine =
    binding === 'starved'
      ? fillCopy(gc.starvedLine ?? '', vars)
      : binding === 'gunLimited'
        ? fillCopy(gc.gunLimitedLine ?? '', vars)
        : binding === 'noGun'
          ? (gc.noGunLine ?? '')
          : fillCopy(gc.firingLine ?? '', vars);
  const tip =
    binding === 'starved'
      ? (gc.starvedTip ?? null)
      : binding === 'gunLimited'
        ? (gc.gunLimitedTip ?? null)
        : null;
  const bn = binding === 'starved' ? bottleneck(s, d, bid) : null;
  return {
    kind: 'gallery',
    bid,
    title: gc.title ?? '',
    gun,
    queue: {
      tiers: g.queue.map((q) => q.tier),
      cap,
      text: fillCopy(gc.queue ?? '', { n: g.queue.length, cap }),
    },
    practice: g.manual ? g.practice : null,
    income: fillCopy(gc.incomeLine ?? '', {
      income: money(f, st.income),
      potential: money(f, st.potential),
    }),
    binding,
    bindingText,
    bindingLine,
    tip,
    advice: bn ? fillCopy(gc.advice ?? '{action}', { action: bottleneckAction(f, bn) }) : null,
  };
}

// ---- Crate card -----------------------------------------------------------------------------------------

export interface CrateModel {
  kind: 'crate';
  bid: number;
  title: string;
  level: number;
  /** "12/32 items" */
  fill: string;
  rows: { tier: number; name: string; count: number; icon: IconKey }[];
  /** Sell all: the value at the Crate rate, null when empty. */
  sellAll: { value: number; text: string } | null;
  build: boolean;
  /** Build mode: selling the Crate itself (100% refund). */
  sellBuilding: { value: number; text: string } | null;
}

export function crateModel(f: Fmt, s: SimState, bid: number, mode: Mode): CrateModel | null {
  const b = s.buildings.find((x) => x.id === bid);
  if (b?.type !== 'crate') return null;
  const cb = b as CrateB;
  const d = f.defs;
  const c = f.copy.cards;
  const cap = d.buildings.crate.capacity[cb.level - 1] ?? 0;
  const total = cb.counts.reduce((a, n) => a + n, 0);
  const rows: CrateModel['rows'] = [];
  if (cb.fam !== null) {
    for (let t = cb.counts.length; t >= 1; t--) {
      const n = cb.counts[t - 1] ?? 0;
      if (n <= 0) continue;
      rows.push({
        tier: t,
        name: f.names.item(cb.fam, t),
        count: n,
        icon: { k: 'item', fam: cb.fam, tier: t },
      });
    }
  }
  const sellValue = crateSellValue(s, d, bid) ?? 0;
  let sellBuilding: CrateModel['sellBuilding'] = null;
  if (mode === 'build') {
    const out = resolveDrop(s, d, { k: 'cell', x: b.x, y: b.y, id: b.id }, { k: 'sell' }, true);
    if (out.k === 'bsell')
      sellBuilding = {
        value: out.refund,
        text: fillCopy(c.sellFor ?? '', { value: money(f, out.refund) }),
      };
  }
  return {
    kind: 'crate',
    bid,
    title: f.names.building('crate', null),
    level: cb.level,
    fill: fillCopy(c.crateCap ?? '', { n: total, cap }),
    rows,
    sellAll:
      total > 0
        ? { value: sellValue, text: fillCopy(c.crateSellAll ?? '', { value: money(f, sellValue) }) }
        : null,
    build: mode === 'build',
    sellBuilding,
  };
}

// ---- Ghost card -----------------------------------------------------------------------------------------

function ghostNeeds(f: Fmt, s: SimState, bid: number): string[] {
  const rows = ghostView(s, f.defs, bid) ?? [];
  return rows
    .filter((r) => r.filled < r.n)
    .map((r) =>
      fillCopy(f.copy.cards.reqItem ?? '{n}× {name}', {
        n: r.n - r.filled,
        name: f.names.item(r.fam, r.tier),
      }),
    );
}

export interface GhostModel {
  kind: 'ghost';
  bid: number;
  title: string;
  icon: IconKey;
  rows: { text: string; done: boolean; icon: IconKey }[];
  paid: string;
  cancel: string;
}

export function ghostModel(f: Fmt, s: SimState, bid: number): GhostModel | null {
  const b = s.buildings.find((x) => x.id === bid);
  const rows = ghostView(s, f.defs, bid);
  if (!b || rows === null) return null;
  const c = f.copy.cards;
  return {
    kind: 'ghost',
    bid,
    title: f.names.building(b.type, b.type === 'deposit' ? b.ore : null),
    icon: { k: 'building', type: b.type, ore: b.type === 'deposit' ? b.ore : null, level: b.level },
    rows: rows.map((r) => ({
      text: `${fillCopy(c.reqItem ?? '{n}× {name}', { n: r.n, name: f.names.item(r.fam, r.tier) })} · ${r.filled}/${r.n}`,
      done: r.filled >= r.n,
      icon: { k: 'item', fam: r.fam, tier: r.tier },
    })),
    paid: fillCopy(c.ghostPaid ?? '', { value: money(f, b.paid) }),
    cancel: c.ghostCancel ?? '',
  };
}

// ---- Expansion card -------------------------------------------------------------------------------------

export interface ExpansionModel {
  kind: 'expansion';
  id: ExpansionId | null;
  title: string;
  price: string | null;
  eta: string | null;
  /** The Expand button's label and whether it works now (null reason = buyable). */
  button: string | null;
  block: string | null;
}

export function expansionModel(f: Fmt, s: SimState): ExpansionModel {
  const c = f.copy.cards;
  const next = expansions(s, f.defs).find((e) => e.state === 'next') ?? null;
  if (!next)
    return {
      kind: 'expansion',
      id: null,
      title: c.maxLevel ?? '',
      price: null,
      eta: null,
      button: null,
      block: null,
    };
  const size = `${next.x1 - next.x0 + 1}×${next.y1 - next.y0 + 1}`;
  const eta = etaText(f.copy, etaFromSec(next.etaSec, f.eta));
  return {
    kind: 'expansion',
    id: next.id,
    title: fillCopy(c.expand ?? '', { size }),
    price: money(f, next.cost),
    eta,
    button: fillCopy(c.expandBuy ?? '', { price: money(f, next.cost) }),
    block: next.block === null ? null : copyText(f.copy, `reason.${next.block}`),
  };
}

/** "$38/s" for cards that show a rate. */
export function rateText(f: Fmt, n: number): string {
  return perSec(f, n);
}

export type CardModel =
  | ItemModel
  | MachineModel
  | GalleryModel
  | CrateModel
  | GhostModel
  | ExpansionModel;

/** The model of the card `t` opens in `mode`, or null (gone). */
export function cardModel(f: Fmt, s: SimState, t: CardTarget, mode: Mode): CardModel | null {
  const kind = cardKindOf(s, t, mode);
  switch (kind) {
    case null:
      return null;
    case 'item':
      return t.k === 'item' ? itemModel(f, s, t.id) : null;
    case 'expansion':
      return expansionModel(f, s);
    case 'ghost':
      return t.k === 'building' ? ghostModel(f, s, t.id) : null;
    case 'crate':
      return t.k === 'building' ? crateModel(f, s, t.id, mode) : null;
    case 'gallery':
      return t.k === 'building' ? galleryModel(f, s, t.id) : null;
    case 'machine':
      return t.k === 'building' ? machineModel(f, s, t.id, mode) : null;
  }
}
